import { useEffect, useState } from "react";
import { isNil } from "lodash";
import {
  CoachProfileModel,
  CoachProfileResponse,
} from "app/models/scheduling/CoachProfileModel";
import {
  FacilityModel,
  FacilityResponse,
} from "app/models/scheduling/FacilityModel";
import { LessonRateModel } from "app/models/scheduling/LessonRateModel";

interface IndexedCoaches {
  [id: string]: CoachProfileModel[];
}

interface IndexedPricingValues {
  [id: string]: LessonRateModel[];
}

interface IndexedRates {
  [id: string]: LessonRateModel[];
}

interface AggregatedRates {
  [id: string]: {
    [id: number]: {
      coachIds: string[];
      value: string;
      label: string;
      duration: string;
      durationInMinutes: number;
      rate: number;
      onlinePaymentsActive: boolean;
      collectType: string;
    };
  };
}

const generateDefaultDurations = (coachId: string): LessonRateModel[] => {
  return [
    // @ts-ignore:next-line
    new LessonRateModel({
      coachProfileId: coachId,
      duration: "30 mins",
      durationInMinutes: 30,
      rate: 0,
      defaultRate: false,
      onlinePaymentsActive: false,
      collectType: 'offline',
    }),
    // @ts-ignore:next-line
    new LessonRateModel({
      coachProfileId: coachId,
      duration: "60 mins",
      durationInMinutes: 60,
      rate: 0,
      defaultRate: true,
      onlinePaymentsActive: false,
      collectType: 'offline',
    }),
  ];
};

const parseFacilities = (facilities: FacilityResponse[]) => {
  if (isNil(facilities)) {
    return [];
  }

  return facilities.map((facility) => new FacilityModel(facility));
};

const parseCoaches = (coaches: CoachProfileResponse[]): IndexedCoaches => {
  if (isNil(coaches)) {
    return {};
  }
  const indexedCoaches: IndexedCoaches = {};
  coaches
    .sort((a, b) => a.orderNumber - b.orderNumber)
    .forEach((coach) => {
      if (!indexedCoaches[coach.facilityId]) {
        indexedCoaches[coach.facilityId] = [];
      }
      indexedCoaches[coach.facilityId].push(new CoachProfileModel(coach));
    });

  return indexedCoaches;
};

const parsePricingValues = (
  coaches: CoachProfileModel[],
): IndexedPricingValues => {
  if (isNil(coaches)) {
    return {};
  }

  const pricingValues: IndexedPricingValues = {};

  coaches.forEach((coach) => {
    const rates = coach.lessonRates.map(
      (lr) => new LessonRateModel(lr),
    );
    rates.sort((a, b) => {
      const orderA = a.orderNumber ?? Number.MAX_SAFE_INTEGER;
      const orderB = b.orderNumber ?? Number.MAX_SAFE_INTEGER;
      return orderA - orderB;
    });
    pricingValues[coach.id] = rates;
  });

  return pricingValues;
};

const parseRates = (
  coaches: CoachProfileModel[],
): [IndexedRates, AggregatedRates] => {
  if (isNil(coaches)) {
    return [{}, {}];
  }

  const rates: IndexedRates = {};
  const aggregatedRates: AggregatedRates = {};

  coaches.forEach((coach) => {
    if (coach.lessonRates.length === 0) {
      rates[coach.id] = generateDefaultDurations(coach.id);
    } else {
      rates[coach.id] = [...coach.lessonRates].sort((a, b) => {
        const orderA = a.orderNumber ?? Number.MAX_SAFE_INTEGER;
        const orderB = b.orderNumber ?? Number.MAX_SAFE_INTEGER;
        return orderA - orderB;
      });
    }

    if (!aggregatedRates[coach.facilityId]) {
      aggregatedRates[coach.facilityId] = {};
    }

    rates[coach.id].forEach((rate) => {
      if (!aggregatedRates[coach.facilityId][rate.durationInMinutes]) {
        aggregatedRates[coach.facilityId][rate.durationInMinutes] = {
          coachIds: [],
          value: `default-${rate.durationInMinutes}`,
          label: rate.duration,
          duration: rate.duration,
          durationInMinutes: rate.durationInMinutes,
          rate: 0,
          onlinePaymentsActive: rate.onlinePaymentsActive,
          collectType: rate.collectType,
        };
      }

      aggregatedRates[coach.facilityId][rate.durationInMinutes].coachIds.push(
        rate.coachProfileId,
      );
    });
  });

  return [rates, aggregatedRates];
};

export default function useBookSessionData(fetcher, isPublicPage = false) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [bookLessonData, setBookLessonData] = useState({
    facilities: [],
    coaches: {},
    rates: {},
    aggregatedRates: {},
    pricingValues: {},
    public: false,
  });

  useEffect(() => {
    setLoading(true);
    fetcher()
      .then((data: [FacilityResponse[], CoachProfileResponse[]]) => {
        if (!data) {
          return;
        }
        const [facilities, coaches] = data;

        const parsedFacilities = parseFacilities(facilities);

        const parsedCoaches = parseCoaches(coaches);
        // @ts-ignore:next-line
        const coachModels = Object.values(parsedCoaches).flat();

        const parsedPricingValues = parsePricingValues(coachModels);
        const [parsedRates, aggregatedRates] = parseRates(coachModels);

        setBookLessonData({
          facilities: parsedFacilities,
          coaches: parsedCoaches,
          pricingValues: parsedPricingValues,
          rates: parsedRates,
          public: isPublicPage,
          aggregatedRates,
        });

        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, [fetcher, isPublicPage]);

  return [loading, bookLessonData, error];
}
